import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, AlertCircle, CheckCircle2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CSVRow {
  country: string;
  city?: string;
  status: string;
  tagline?: string;
  tourist_stay_default?: string;
  remote_work_visa?: string;
  key_requirements?: string;
  tax_residency_trigger?: string;
  notes_1_5_months?: number;
  ease_1_5_months?: number;
  ease_compliance?: number;
  cost_of_living?: number;
  attractiveness?: number;
  employee_notes?: string;
  legal_risks?: string;
  hr_guidelines?: string;
  company_caps?: string;
  cost_impact_estimate?: string;
  weekly_cost_excluding_flight?: number;
  round_trip_flight_cost_from_france?: number;
  image_url?: string;
  renest_notes?: string;
}

interface ImportResult {
  activated: number;
  deactivated: number;
  errors: Array<{ row: number; country: string; error: string }>;
}

interface Props {
  onImportComplete: () => void;
}

const AdminCSVCountryImport = ({ onImportComplete }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const validateAndParseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV vide ou invalide');
    }

    // Robust CSV line parser (handles commas inside quotes)
    const parseCSVLine = (line: string) => {
      const matches = line.match(/(")([^"]|"")*"|[^,]+|(?=,|$)/g) || [];
      return matches
        .map((m) => m.trim())
        .map((m) => m.replace(/^"|"$/g, '').replace(/""/g, '"'));
    };

    // Normalize and map header names (accept FR/EN variants and notes)
    const normalize = (s: string) =>
      s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove accents
        .replace(/\(.*?\)/g, '') // remove parentheses content
        .replace(/[^a-z0-9]+/g, ' ') // non-alphanum to spaces
        .trim();

    const mapHeader = (h: string): string | null => {
      const n = normalize(h);
      if (/(^|\s)(country|pays)(\s|$)/.test(n)) return 'country';
      if (/(^|\s)(city|ville)(\s|$)/.test(n)) return 'city';
      if (/(^|\s)(status|statut)(\s|$)/.test(n)) return 'status';
      if (n.includes('tagline') || n.includes('slogan')) return 'tagline';
      if (n.includes('tourist') || n.includes('sejour touristique') || n.includes('sejour touriste')) return 'tourist stay default';
      if (n.includes('remote work visa') || n.includes('visa remote') || n.includes('visa teletravail')) return 'remote work visa';
      if (n.includes('key requirements') || n.includes('exigences cle')) return 'key requirements';
      if (n.includes('tax residency trigger') || n.includes('enregistrement') || n.includes('regles fiscales')) return 'tax residency trigger';
      if (n.includes('notes 1 5') || n.includes('notes 1 5 months')) return 'notes 1-5 months';
      if (n.includes('ease 1 5 months') || n.includes('ease 1 5m')) return 'ease 1-5 months';
      if (n.includes('ease compliance')) return 'ease compliance';
      if (n.includes('cost of living') || n.includes('cout de vie')) return 'cost of living';
      if (n.includes('attractiveness') || n.includes('attractivite')) return 'attractiveness';
      if (n.includes('employee notes') || n.includes('notes employes')) return 'employee notes';
      if (n.includes('legal risks') || n.includes('risques juridiques')) return 'legal risks';
      if (n.includes('hr guidelines') || n.includes('directrices rh') || n.includes('directives rh')) return 'hr guidelines';
      if (n.includes('company caps') || n.includes('caps entreprise')) return 'company caps';
      if (n.includes('impact cout') || n.includes('cout entreprise')) return 'cost impact';
      if (n.includes('cout hebdo') || n.includes('weekly cost')) return 'weekly cost';
      if (n.includes('a r depuis') || n.includes('flight cost') || n.includes('round trip')) return 'flight cost';
      if (n.includes('image url') || n.includes('url image')) return 'image url';
      if (n.includes('renest notes') || n.includes('notes renest')) return 'renest notes';
      return null; // unknown column
    };

    const rawHeader = parseCSVLine(lines[0]);
    const header = rawHeader.map(mapHeader);

    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row: Record<string, string> = {};

      header.forEach((key, index) => {
        if (!key) return; // skip unknown columns
        row[key] = (values[index] ?? '').trim();
      });

      const countryName = row['country'];
      if (countryName) {
        const parseNumber = (val: string) => {
          const num = parseInt((val || '').toString().trim(), 10);
          return !isNaN(num) && num >= 1 && num <= 5 ? num : undefined;
        };
        
        const parseDecimal = (val: string) => {
          const num = parseFloat((val || '').toString().trim());
          return !isNaN(num) ? num : undefined;
        };

        rows.push({
          country: countryName,
          city: row['city'] || undefined,
          status: row['status'] || '',
          tagline: row['tagline'] || undefined,
          tourist_stay_default: row['tourist stay default'] || undefined,
          remote_work_visa: row['remote work visa'] || undefined,
          key_requirements: row['key requirements'] || undefined,
          tax_residency_trigger: row['tax residency trigger'] || undefined,
          notes_1_5_months: parseNumber(row['notes 1-5 months']),
          ease_1_5_months: parseNumber(row['ease 1-5 months']),
          ease_compliance: parseNumber(row['ease compliance']),
          cost_of_living: parseNumber(row['cost of living']),
          attractiveness: parseNumber(row['attractiveness']),
          employee_notes: row['employee notes'] || undefined,
          legal_risks: row['legal risks'] || undefined,
          hr_guidelines: row['hr guidelines'] || undefined,
          company_caps: row['company caps'] || undefined,
          cost_impact_estimate: row['cost impact'] || undefined,
          weekly_cost_excluding_flight: parseDecimal(row['weekly cost']),
          round_trip_flight_cost_from_france: parseDecimal(row['flight cost']),
          image_url: row['image url'] || undefined,
          renest_notes: row['renest notes'] || undefined,
        });
      }
    }

    return rows;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez sélectionner un fichier CSV"
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const text = await file.text();
      const rows = validateAndParseCSV(text);

      const result: ImportResult = {
        activated: 0,
        deactivated: 0,
        errors: []
      };

      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Get all countries from database
      const { data: allCountries } = await supabase
        .from('countries')
        .select('id, name');

      const countryMap = new Map(
        (allCountries || []).map(c => [c.name.toLowerCase(), c.id])
      );

      // Process each row
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const countryId = countryMap.get(row.country.toLowerCase());

        const shouldActivate = row.status.toLowerCase() === 'open' || 
                              row.status.toLowerCase() === 'active' ||
                              row.status.toLowerCase() === 'ouvert';

        const countryData = {
          is_active: shouldActivate,
          city: row.city || null,
          tagline: row.tagline || null,
          tourist_stay_default: row.tourist_stay_default || null,
          remote_work_visa_option: row.remote_work_visa || null,
          key_requirements: row.key_requirements || null,
          tax_residency_trigger: row.tax_residency_trigger || null,
          notes_1_5_months: row.notes_1_5_months || null,
          ease_1_5_months: row.ease_1_5_months || null,
          ease_compliance: row.ease_compliance || null,
          cost_of_living: row.cost_of_living || null,
          attractiveness_rating: row.attractiveness || null,
          employee_notes: row.employee_notes || null,
          legal_compliance_risks: row.legal_risks || null,
          hr_guidelines: row.hr_guidelines || null,
          company_caps_observed: row.company_caps || null,
          cost_impact_estimate: row.cost_impact_estimate || null,
          weekly_cost_excluding_flight: row.weekly_cost_excluding_flight || null,
          round_trip_flight_cost_from_france: row.round_trip_flight_cost_from_france || null,
          image_url: row.image_url || null,
          renest_notes: row.renest_notes || null
        };

        if (countryId) {
          // Update existing country
          const { error } = await supabase
            .from('countries')
            .update(countryData)
            .eq('id', countryId);

          if (error) {
            result.errors.push({
              row: i + 2,
              country: row.country,
              error: error.message
            });
          } else {
            if (shouldActivate) {
              result.activated++;
            } else {
              result.deactivated++;
            }
          }
        } else {
          // Insert new country
          const { error } = await supabase
            .from('countries')
            .insert({
              name: row.country,
              ...countryData
            });

          if (error) {
            result.errors.push({
              row: i + 2,
              country: row.country,
              error: error.message
            });
          } else {
            if (shouldActivate) {
              result.activated++;
            } else {
              result.deactivated++;
            }
          }
        }
      }

      // Save import history
      await supabase
        .from('destination_import_export_history')
        .insert({
          company_id: '00000000-0000-0000-0000-000000000000', // Special UUID for admin actions
          admin_user_id: user.id,
          operation_type: 'admin_import',
          file_name: file.name,
          countries_opened: result.activated,
          countries_closed: result.deactivated,
          total_processed: rows.length,
          errors_count: result.errors.length,
          error_details: result.errors.length > 0 ? result.errors : null
        });

      setImportResult(result);
      onImportComplete();

      toast({
        title: "Import terminé",
        description: `${result.activated} pays activés, ${result.deactivated} pays désactivés, ${result.errors.length} erreurs`
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'import",
        description: error.message
      });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Country,City,Status,Tagline,Tourist Stay Default,Remote Work Visa,Key Requirements,Tax Residency Trigger,Notes 1-5 Months (1-5),Ease 1-5 Months (1-5),Ease Compliance (1-5),Cost of Living (1-5),Attractiveness (1-5),Employee Notes,Legal Risks,HR Guidelines,Company Caps,Impact coût (entreprise),Coût hebdo (hors vol) €,A/R depuis FR (est.) €,Image URL,Renest Notes
France,Paris,open,"La belle vie","180 days","Yes - Digital Nomad Visa","Valid passport, proof of income","183 days",4,4,3,3,5,"Beautiful country for remote work","Low risk with proper documentation","Ensure compliance with local labor laws","Most companies allow 30-90 days","€2000/month est.",450,600,"https://example.com/france.jpg","Recommended destination for first-time remote workers"
Spain,Barcelona,open,"Sol y playa","90 days","Yes - Digital Nomad Visa","Digital nomad visa application","183 days",5,5,4,4,5,"Great digital nomad destination","Low risk","Remote work authorization required","30-90 days typical","€1800/month est.",400,500,"https://example.com/spain.jpg","Popular for tech workers"
Germany,Berlin,close,"Efficiency and innovation","90 days","No","Work permit required","183 days",3,3,2,3,4,"Not available yet","Requires work permit","Complex labor regulations","Limited availability","€2200/month est.",500,700,"https://example.com/germany.jpg","Contact HR before requesting"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_import_countries.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>
        <Upload className="mr-2 h-4 w-4" />
        Importer un fichier CSV
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importer des pays via CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Format CSV requis:</strong> Country (requis), City, Status (open/close), Tagline, Tourist Stay Default, Remote Work Visa, Key Requirements, Tax Residency Trigger, Notes 1-5 Months (1-5), Ease 1-5 Months (1-5), Ease Compliance (1-5), Cost of Living (1-5), Attractiveness (1-5), Employee Notes, Legal Risks, HR Guidelines, Company Caps, Impact coût (entreprise), Coût hebdo (hors vol) €, A/R depuis FR (est.) €, Image URL, Renest Notes
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                Télécharger le modèle CSV
              </Button>
              <div className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="admin-csv-upload"
                  disabled={importing}
                />
                <label htmlFor="admin-csv-upload">
                  <Button 
                    variant="default" 
                    className="w-full" 
                    disabled={importing}
                    asChild
                  >
                    <span>
                      {importing ? 'Import en cours...' : 'Sélectionner un fichier CSV'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {importResult && (
              <Card className="p-4 space-y-3">
                <h3 className="font-semibold">Résumé de l'import</h3>
                
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{importResult.activated} pays activés</span>
                </div>

                <div className="flex items-center gap-2 text-blue-600">
                  <X className="h-4 w-4" />
                  <span>{importResult.deactivated} pays désactivés</span>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>{importResult.errors.length} erreurs</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
                      {importResult.errors.map((err, idx) => (
                        <div key={idx} className="text-red-600">
                          Ligne {err.row}: {err.country} - {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminCSVCountryImport;
